import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "../../chat/api";
import SpreadsheetViewer from "../../Spreadsheets/SpreadsheetViewer";
import CopilotSidePanel from "./CopilotSidePanel";

/* The project's documents, and the original page behind an answer.
 *
 * One column, two ways in. "Chat with project" opens it on the project's first
 * document with the whole file list underneath, to read and switch between while
 * you ask. Clicking a citation in an answer jumps the same viewer to that file,
 * at the page the passage came from. Both are the old project chat's right-hand
 * side (pages/RFP_Manager/Projects/ProjectChat.jsx), so nothing about reading a
 * source changed when the chat moved into Sensa.
 *
 * Opened the way the project chat opened a source: the file's own URL with
 * `#page=N&search="…"` appended, handed to the browser's PDF viewer in an
 * iframe. Deliberately not a rendering of our own — the native viewer brings
 * find, zoom, print, page navigation and text selection for free, and matching
 * the behaviour that already exists elsewhere in the product matters more here
 * than any of them.
 *
 * WHY NOT THE RETRIEVED CHUNK. The first version of this panel showed the text
 * the search matched, which is what the model read and therefore seemed like
 * the honest thing to show. It was unreadable: extraction flattens a table of
 * contents into a column of dotted leaders and strips tables of their rows. A
 * bid team checks a claim by looking at the page it is on.
 *
 * WHY THE PAGE NUMBER IS RIGHT. The stored page index counts from zero — for a
 * 67-page tender the values run 0..66, and every chunk carrying the document's
 * own printed "Page N of 67" footer has N == stored + 1. The server adds the
 * one (see _page_1based), so a citation lands on the page the reader would
 * point at rather than the one before it.
 */

/* The opening words of the passage, as a find query for the viewer.
 *
 * Lifted from ProjectChat's getSearchPhrase so both surfaces highlight the same
 * way. Five words: enough to be unique on a page, short enough to survive the
 * whitespace and ligature differences between the extracted text and what the
 * viewer is searching — a long exact phrase almost never matches.
 */
const searchPhrase = (raw) => {
  if (!raw) return "";
  const clean = String(raw)
    .replace(/\\n/g, " ")
    .replace(/[^a-zA-Z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return clean.split(" ").slice(0, 5).join(" ");
};

/* Opening zoom, as a percentage.
 *
 * Left to itself the viewer fits the page to the frame, and in a 620px column
 * that lands around 53% — legible enough to confirm you are on the right page
 * and too small to actually read a clause, which is the thing people opened it
 * to do. 75% is the largest step that still fits a portrait page across this
 * panel without a horizontal scrollbar: A4 renders about 595px wide at 75%, and
 * US Letter about 612px, both inside the column.
 */
const OPEN_ZOOM = 75;

const baseUrl = (url) => String(url || "").split("#")[0];
const extension = (url) => baseUrl(url).split("?")[0].split(".").pop().toLowerCase();
const IMAGES = ["jpg", "jpeg", "png", "gif", "webp"];
const SHEETS = ["xls", "xlsx", "csv"];

/* url#page=N&zoom=75&search="phrase" — the Adobe open-parameter convention
 * every PDF viewer understands. Any existing hash is dropped first: appending
 * to a URL that already carries one produces `#page=2#page=9`, and the viewer
 * honours the first. */
const viewerUrl = (source) => {
  if (!source?.url) return "";
  const base = baseUrl(source.url);
  if (extension(base) !== "pdf") return base;

  const params = [];
  if (source.page) params.push(`page=${source.page}`);
  params.push(`zoom=${OPEN_ZOOM}`);
  const phrase = searchPhrase(source.text);
  if (phrase) params.push(`search=${encodeURIComponent(`"${phrase}"`)}`);

  return `${base}#${params.join("&")}`;
};

const ICON = { fill: "none", stroke: "currentColor", strokeWidth: 2.2, strokeLinecap: "round", strokeLinejoin: "round" };

/* One citation, as a jump target. Numbered to match the mark in the prose
 * exactly, so the eye can carry a number from the sentence to this strip
 * without re-reading either. */
const CiteChip = ({ source, active, onPick }) => (
  <button
    type="button"
    onClick={() => onPick(source)}
    aria-current={active ? "true" : undefined}
    title={`${source.file}${source.page ? ` · page ${source.page}` : ""}`}
    className={`flex h-[22px] min-w-[22px] flex-none items-center justify-center rounded-md px-1.5 text-[11px] font-semibold tabular-nums transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A04A97] motion-reduce:transition-none ${
      active
        ? "bg-[#A04A97] text-white"
        : "bg-[#A04A97]/[0.11] text-[#8B3F83] hover:bg-[#A04A97]/[0.22]"
    }`}
  >
    {source.n}
  </button>
);

/* The file being shown, rendered the way its type needs. PDFs and anything else
 * the browser can draw go in a frame; images and sheets have their own views;
 * Word, PowerPoint and the like go through Office's embed, as the project chat
 * did, since a bare URL to one of those downloads instead of opening. */
const Viewer = ({ view, src }) => {
  const ext = extension(view.url);
  const title = `${view.file || "Document"}${view.page ? `, page ${view.page}` : ""}`;

  if (IMAGES.includes(ext)) {
    return (
      <div className="flex h-full w-full items-center justify-center overflow-auto bg-[#F4F1F6]">
        <img src={src} alt={title} className="max-h-full max-w-full object-contain p-4" />
      </div>
    );
  }
  if (SHEETS.includes(ext)) return <SpreadsheetViewer fileUrl={src} />;
  if (ext === "pdf") {
    /* `key` is load-bearing. Changing only the hash of an iframe's src does NOT
     * make it navigate, so switching from page 9 to page 14 of the same
     * document would leave the viewer exactly where it was. Keying on the full
     * URL remounts the frame and the jump happens. */
    return <iframe key={src} src={src} title={title} className="h-full w-full border-none bg-white" />;
  }
  return (
    <iframe
      key={src}
      src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(src)}`}
      title={title}
      className="h-full w-full border-none bg-white"
    />
  );
};

/* The project's files, as tabs under the viewer. Not shown for a single file:
 * the header already names it, and a lone tab is a control that cannot do
 * anything. */
const DocumentStrip = ({ docs, currentUrl, onPick }) => (
  <div className="flex flex-none gap-2 overflow-x-auto border-t border-[#18101C]/[0.07] bg-white px-3 py-2.5 custom-scrollbar">
    {docs.map((d) => {
      const active = baseUrl(d.url) === baseUrl(currentUrl);
      return (
        <button
          key={d.key || d.id}
          type="button"
          onClick={() => onPick(d)}
          aria-current={active ? "true" : undefined}
          title={d.name}
          className={`flex max-w-[190px] flex-none items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-[12.5px] font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A04A97] ${
            active
              ? "border-[#A04A97] bg-[#A04A97]/[0.08] text-[#8B3F83]"
              : "border-[#18101C]/10 bg-white text-[#42404A] hover:border-[#A04A97]/40"
          }`}
        >
          <svg aria-hidden="true" width="13" height="13" viewBox="0 0 24 24" className="flex-none" {...ICON}>
            <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
            <polyline points="14 3 14 8 19 8" />
          </svg>
          <span className="truncate">{d.name}</span>
        </button>
      );
    })}
  </div>
);

const CopilotSourcePanel = ({ sources, activeN, projectId, projectName, visible, onClose, onManage }) => {
  const list = useMemo(
    // Numeric, so [10] sorts after [9] rather than between [1] and [2].
    () => [...(sources || [])].sort((a, b) => Number(a.n) - Number(b.n)),
    [sources]
  );

  /* The project's files, from the same endpoint the project panel reads, so a
   * document is the same document on both. Fetched once per project; the viewer
   * opens the original file whatever stage reading it has reached. */
  const [docs, setDocs] = useState([]);
  const [docsLoading, setDocsLoading] = useState(!!projectId);
  useEffect(() => {
    if (!projectId) {
      setDocs([]);
      setDocsLoading(false);
      return undefined;
    }
    let cancelled = false;
    setDocsLoading(true);
    api
      .get(`/api/v1/rfp/folders/${projectId}/copilot/files/`)
      .then((res) => {
        if (!cancelled) setDocs((res.data?.documents || []).filter((d) => d?.url));
      })
      .catch(() => {
        if (!cancelled) setDocs([]);
      })
      .finally(() => {
        if (!cancelled) setDocsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  /* What the viewer is showing: a cited passage, or a file picked from the
   * strip. A citation wins when one is clicked; otherwise the first file. */
  const cited = useMemo(
    () => list.find((s) => Number(s.n) === Number(activeN)) || null,
    [list, activeN]
  );
  const [view, setView] = useState(() => cited || list[0] || null);

  /* Clicking a different mark in the prose while the panel is open moves it,
   * rather than closing and reopening the panel — the document list is the
   * same and only the target changed. */
  useEffect(() => {
    if (cited) setView(cited);
  }, [cited]);

  useEffect(() => {
    if (!view && docs.length) setView({ file: docs[0].name, url: docs[0].url, page: null, text: "" });
  }, [docs, view]);

  const pickCitation = useCallback((s) => setView(s), []);
  const pickDocument = useCallback(
    (d) => setView({ file: d.name, url: d.url, page: null, text: "", project_name: projectName }),
    [projectName]
  );

  const src = viewerUrl(view);
  const project = view?.project_name || projectName;

  return (
    <CopilotSidePanel visible={visible} label="Project documents" onClose={onClose} width={620}>
      <header className="flex-none border-b border-[#18101C]/[0.07] px-4 pb-3 pt-4">
        <div className="flex items-start gap-2">
          <div className="min-w-0 flex-1">
            <h2
              className="truncate text-[14.5px] font-semibold leading-snug tracking-[-0.01em] text-[#18101C]"
              title={view?.file}
            >
              {view?.file || (docsLoading ? "Loading documents…" : "Documents")}
            </h2>
            <p className="mt-0.5 truncate text-[11.5px] text-[#77707E]">
              {[project, view?.page ? `Page ${view.page}` : ""].filter(Boolean).join(" · ")}
            </p>
          </div>

          {/* The old project panel: where files are added and their reading
              progress is watched. Viewing is the common case, so it is one
              step away rather than the first thing on screen. */}
          {onManage && projectId && (
            <button
              type="button"
              onClick={() => onManage({ projectId, projectName })}
              className="flex h-7 flex-none items-center rounded-lg px-2 text-[12px] font-semibold text-[#8B3F83] transition-colors hover:bg-[#A04A97]/[0.1] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A04A97]"
            >
              Manage files
            </button>
          )}

          {/* Out to a full tab, for reading rather than checking. The panel is
              620px because it shares the column with the conversation; a
              contract clause with a wide table wants the whole window. */}
          {src && (
            <a
              href={src}
              target="_blank"
              rel="noopener noreferrer"
              title="Open in a new tab"
              aria-label="Open the document in a new tab"
              className="flex h-7 w-7 flex-none items-center justify-center rounded-lg text-[#6B6472] transition-colors hover:bg-[#18101C]/[0.06] hover:text-[#18101C] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A04A97]"
            >
              <svg aria-hidden="true" width="13" height="13" viewBox="0 0 24 24" {...ICON}>
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                <polyline points="15 3 21 3 21 9" />
                <line x1="10" y1="14" x2="21" y2="3" />
              </svg>
            </a>
          )}

          <button
            type="button"
            onClick={onClose}
            aria-label="Close panel"
            className="flex h-7 w-7 flex-none items-center justify-center rounded-lg text-[#6B6472] transition-colors hover:bg-[#18101C]/[0.06] hover:text-[#18101C] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A04A97]"
          >
            <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" {...ICON}>
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Every citation in the answer, as jump targets. Only when there is
            more than one — a single chip is a control that cannot do anything. */}
        {list.length > 1 && (
          <div className="mt-2.5 flex flex-wrap items-center gap-1">
            <span className="mr-0.5 text-[11px] text-[#77707E]">Cited</span>
            {list.map((s) => (
              <CiteChip
                key={s.n}
                source={s}
                active={Number(s.n) === Number(view?.n)}
                onPick={pickCitation}
              />
            ))}
          </div>
        )}
      </header>

      <div className="min-h-0 flex-1 bg-[#F4F1F6]">
        {src ? (
          <Viewer view={view} src={src} />
        ) : docsLoading ? (
          <div className="flex h-full flex-col items-center justify-center text-[#77707E]">
            <span className="mb-2 h-6 w-6 animate-spin rounded-full border-2 border-[#A04A97] border-t-transparent motion-reduce:animate-none" />
            <p className="text-[12.5px]">Loading documents…</p>
          </div>
        ) : (
          /* No document to open — so the passage stops being a fallback and
             becomes the content. */
          <div className="m-3 rounded-xl border border-[#18101C]/[0.07] bg-white p-3">
            <p className="text-[11.5px] text-[#77707E]">
              {view
                ? "This passage has no document attached."
                : projectId
                  ? "This project has no documents yet."
                  : "This answer has no passages attached."}
              {view?.page ? ` It is on page ${view.page}.` : ""}
            </p>
            {view?.text && (
              <p className="mt-2 whitespace-pre-wrap text-[12.5px] leading-[1.7] text-[#42404A]">
                {view.text}
              </p>
            )}
          </div>
        )}
      </div>

      {docs.length > 1 && <DocumentStrip docs={docs} currentUrl={view?.url} onPick={pickDocument} />}
    </CopilotSidePanel>
  );
};

export default CopilotSourcePanel;
